/**
 * Emergent Marketplace — Phase 1
 * VendorStripeOnboarding.jsx
 * Architectural Layer: Presentation → Vendor
 *
 * Shown in the vendor dashboard after approval — guides the vendor
 * through Stripe Connect setup and shows real-time status.
 * Design: Clean progress ritual — warm whites, sage green confirmation,
 * amber warning states. Font: Libre Baskerville + Source Sans 3.
 */

import { useState, useEffect } from "react";
import {
  CheckCircle, AlertCircle, ExternalLink, RefreshCw,
  CreditCard, Shield, Zap, ChevronRight, Clock
} from "lucide-react";
import { apiFetch } from "./api";

// ─── Styles ──────────────────────────────────────────────────────────────────
const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Libre+Baskerville:ital,wght@0,400;0,700;1,400&family=Source+Sans+3:wght@300;400;600&display=swap');

  .vso-root {
    --bg: #faf9f6;
    --surface: #ffffff;
    --border: #e8e3da;
    --sage: #3d6b50;
    --sage-bg: #eef5f0;
    --sage-border: #b8d6c3;
    --amber: #b05c00;
    --amber-bg: #fef5ea;
    --amber-border: #f0c890;
    --slate: #2a2a2a;
    --slate-mid: #606060;
    --slate-light: #a0a0a0;
    --stripe-blue: #635bff;
    --stripe-blue-light: #ede9ff;
    font-family: 'Source Sans 3', sans-serif;
    color: var(--slate);
  }

  .vso-container {
    max-width: 600px;
    margin: 0 auto;
    padding: 40px 24px;
  }

  /* Status banner */
  .vso-banner {
    border-radius: 16px;
    padding: 24px;
    margin-bottom: 28px;
    border: 1.5px solid;
    display: flex;
    gap: 16px;
    align-items: flex-start;
  }

  .vso-banner.pending  { background: var(--amber-bg); border-color: var(--amber-border); }
  .vso-banner.complete { background: var(--sage-bg);  border-color: var(--sage-border); }
  .vso-banner.connect  { background: var(--stripe-blue-light); border-color: rgba(99,91,255,0.25); }

  .vso-banner-icon {
    width: 40px; height: 40px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  .vso-banner.pending  .vso-banner-icon { background: var(--amber-border); color: var(--amber); }
  .vso-banner.complete .vso-banner-icon { background: var(--sage-border);  color: var(--sage);  }
  .vso-banner.connect  .vso-banner-icon { background: rgba(99,91,255,0.15); color: var(--stripe-blue); }

  .vso-banner-title {
    font-family: 'Libre Baskerville', serif;
    font-size: 17px;
    font-weight: 700;
    margin: 0 0 4px;
  }

  .vso-banner.pending  .vso-banner-title { color: var(--amber); }
  .vso-banner.complete .vso-banner-title { color: var(--sage); }
  .vso-banner.connect  .vso-banner-title { color: var(--stripe-blue); }

  .vso-banner-body {
    font-size: 14px;
    color: var(--slate-mid);
    margin: 0;
    line-height: 1.6;
  }

  /* Steps */
  .vso-steps {
    display: flex;
    flex-direction: column;
    gap: 0;
    margin-bottom: 28px;
  }

  .vso-step {
    display: flex;
    gap: 16px;
    position: relative;
  }

  .vso-step-left {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0;
    flex-shrink: 0;
  }

  .vso-step-icon {
    width: 40px; height: 40px;
    border-radius: 50%;
    border: 2px solid var(--border);
    background: var(--surface);
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--slate-light);
    font-size: 14px;
    font-weight: 700;
    flex-shrink: 0;
    transition: all 0.3s;
    z-index: 1;
  }

  .vso-step-icon.done {
    background: var(--sage);
    border-color: var(--sage);
    color: white;
  }

  .vso-step-icon.active {
    background: var(--stripe-blue);
    border-color: var(--stripe-blue);
    color: white;
    box-shadow: 0 0 0 4px rgba(99,91,255,0.15);
  }

  .vso-step-line {
    width: 2px;
    flex: 1;
    min-height: 24px;
    background: var(--border);
    margin: 4px 0;
  }

  .vso-step-line.done { background: var(--sage); }

  .vso-step-content {
    padding: 8px 0 28px;
    flex: 1;
  }

  .vso-step-title {
    font-family: 'Libre Baskerville', serif;
    font-size: 16px;
    font-weight: 700;
    color: var(--slate);
    margin: 0 0 4px;
  }

  .vso-step-desc {
    font-size: 13px;
    color: var(--slate-mid);
    line-height: 1.6;
    margin: 0 0 12px;
  }

  /* Stripe button */
  .vso-stripe-btn {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    padding: 12px 24px;
    border-radius: 10px;
    background: var(--stripe-blue);
    border: none;
    color: white;
    font-family: 'Source Sans 3', sans-serif;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: opacity 0.2s, transform 0.15s;
    text-decoration: none;
  }

  .vso-stripe-btn:hover { opacity: 0.9; transform: translateY(-1px); }
  .vso-stripe-btn:disabled { opacity: 0.5; cursor: not-allowed; transform: none; }

  /* Requirements list */
  .vso-requirements {
    background: var(--amber-bg);
    border: 1px solid var(--amber-border);
    border-radius: 10px;
    padding: 14px 16px;
    margin-top: 8px;
  }

  .vso-req-title {
    font-size: 12px;
    font-weight: 600;
    color: var(--amber);
    letter-spacing: 0.08em;
    text-transform: uppercase;
    margin: 0 0 8px;
  }

  .vso-req-item {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    color: var(--slate-mid);
    padding: 3px 0;
  }

  /* Payout info card */
  .vso-info-card {
    background: var(--surface);
    border: 1px solid var(--border);
    border-radius: 14px;
    overflow: hidden;
    margin-bottom: 20px;
  }

  .vso-info-card-header {
    padding: 16px 20px;
    border-bottom: 1px solid var(--border);
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .vso-info-card-title {
    font-family: 'Libre Baskerville', serif;
    font-size: 15px;
    font-weight: 700;
    color: var(--slate);
    margin: 0;
  }

  .vso-info-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px 20px;
    border-bottom: 1px solid var(--bg);
    font-size: 14px;
  }

  .vso-info-row:last-child { border-bottom: none; }
  .vso-info-label { color: var(--slate-mid); }
  .vso-info-value { font-weight: 600; color: var(--slate); }

  /* Trust badges */
  .vso-trust {
    display: flex;
    gap: 20px;
    padding: 16px 0;
    justify-content: center;
  }

  .vso-trust-item {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 6px;
    font-size: 11px;
    color: var(--slate-light);
    text-align: center;
    letter-spacing: 0.04em;
  }

  .vso-trust-icon {
    width: 36px; height: 36px;
    border-radius: 50%;
    background: var(--bg);
    border: 1px solid var(--border);
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--slate-mid);
  }

  /* Subscription toggle */
  .vso-sub-toggle {
    background: var(--surface);
    border: 1px solid var(--border);
    border-radius: 14px;
    padding: 20px;
    margin-bottom: 20px;
  }

  .vso-sub-toggle-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 12px;
  }

  .vso-sub-toggle-title {
    font-family: 'Libre Baskerville', serif;
    font-size: 16px;
    font-weight: 700;
    color: var(--slate);
    margin: 0 0 4px;
  }

  .vso-sub-toggle-desc {
    font-size: 13px;
    color: var(--slate-mid);
    margin: 0;
    line-height: 1.5;
  }

  .vso-sub-pill {
    padding: 4px 12px;
    border-radius: 20px;
    font-size: 11px;
    font-weight: 600;
    white-space: nowrap;
  }

  .vso-sub-pill.active   { background: var(--sage-bg); color: var(--sage); border: 1px solid var(--sage-border); }
  .vso-sub-pill.inactive { background: var(--bg);       color: var(--slate-light); border: 1px solid var(--border); }

  .vso-sub-btn {
    width: 100%;
    padding: 11px;
    border-radius: 8px;
    border: 1.5px solid var(--sage);
    background: transparent;
    color: var(--sage);
    font-family: 'Source Sans 3', sans-serif;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;
  }

  .vso-sub-btn:hover { background: var(--sage-bg); }

  .vso-refresh-btn {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 8px 14px;
    border: 1px solid var(--border);
    border-radius: 8px;
    background: none;
    font-size: 13px;
    color: var(--slate-mid);
    cursor: pointer;
    transition: border-color 0.15s;
  }

  .vso-refresh-btn:hover { border-color: var(--slate-mid); }
  .vso-refresh-btn.spinning svg { animation: spin 1s linear infinite; }
  .vso-spin { animation: spin 1s linear infinite; }

  @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
`;

// ─── Main Component ───────────────────────────────────────────────────────────

export default function VendorStripeOnboarding({ vendor, user, onStatusChange }) {
  const [connectStatus, setConnectStatus] = useState(null);
  const [loadingStatus, setLoadingStatus] = useState(true);
  const [loadingLink, setLoadingLink] = useState(false);
  const [error, setError] = useState(null);

  const fetchStatus = async () => {
    setLoadingStatus(true);
    try {
      const data = await apiFetch("/vendor/stripe/status");
      setConnectStatus(data);
    } catch (e) {
      setError("Could not check Stripe status. Please refresh.");
    } finally {
      setLoadingStatus(false);
    }
  };

  useEffect(() => {
    fetchStatus();
  }, []);

  const openOnboarding = async () => {
    setLoadingLink(true);
    setError(null);
    try {
      const data = await apiFetch("/vendor/stripe/onboard", { method: "POST" });
      window.open(data.url, "_blank");
    } catch (e) {
      setError("Could not generate onboarding link. Please try again.");
    } finally {
      setLoadingLink(false);
    }
  };

  const isFullyConnected = connectStatus?.charges_enabled && connectStatus?.payouts_enabled;
  const hasRequirements = (connectStatus?.requirements?.length || 0) > 0;

  // Determine which step we're on
  const step1Done = vendor?.status === "approved";
  const step2Done = connectStatus?.details_submitted;
  const step3Done = isFullyConnected;
  const activeStep = !step1Done ? 1 : !step2Done ? 2 : !step3Done ? 3 : 4;

  return (
    <>
      <style>{styles}</style>
      <div className="vso-root">
        <div className="vso-container">

          {/* Status banner */}
          {isFullyConnected ? (
            <div className="vso-banner complete">
              <div className="vso-banner-icon"><CheckCircle size={20} /></div>
              <div>
                <p className="vso-banner-title">Stripe Connect active</p>
                <p className="vso-banner-body">
                  Your store is fully connected. Sales will be transferred to your bank account automatically after each order.
                </p>
              </div>
            </div>
          ) : vendor?.status !== "approved" ? (
            <div className="vso-banner pending">
              <div className="vso-banner-icon"><Clock size={20} /></div>
              <div>
                <p className="vso-banner-title">Application under review</p>
                <p className="vso-banner-body">
                  Your vendor application is being reviewed. Once approved by the 11/11 Collective Connection team, you'll be able to complete Stripe setup here.
                </p>
              </div>
            </div>
          ) : (
            <div className="vso-banner connect">
              <div className="vso-banner-icon"><CreditCard size={20} /></div>
              <div>
                <p className="vso-banner-title">Stripe setup required</p>
                <p className="vso-banner-body">
                  Complete the Stripe Connect onboarding below to activate payouts. Takes about 5 minutes.
                </p>
              </div>
            </div>
          )}

          {/* Progress steps */}
          <div className="vso-steps">
            {[
              {
                n: 1,
                title: "Application approved",
                desc: "Your store application was reviewed and approved by the 11/11 Collective Connection team.",
                done: step1Done,
              },
              {
                n: 2,
                title: "Connect your Stripe account",
                desc: "Link a bank account so payouts can be sent directly to you after each sale.",
                done: step2Done,
                action: !step2Done && step1Done,
              },
              {
                n: 3,
                title: "Stripe verification complete",
                desc: "Stripe verifies your identity and bank account. Usually instant.",
                done: step3Done,
              },
              {
                n: 4,
                title: "Store is live",
                desc: "Your products are visible in the Marketplace and you can accept payments.",
                done: isFullyConnected,
              },
            ].map((s, i, arr) => (
              <div className="vso-step" key={s.n}>
                <div className="vso-step-left">
                  <div className={`vso-step-icon ${s.done ? "done" : activeStep === s.n ? "active" : ""}`}>
                    {s.done ? <CheckCircle size={18} /> : s.n}
                  </div>
                  {i < arr.length - 1 && (
                    <div className={`vso-step-line ${s.done ? "done" : ""}`} />
                  )}
                </div>
                <div className="vso-step-content">
                  <p className="vso-step-title">{s.title}</p>
                  <p className="vso-step-desc">{s.desc}</p>

                  {/* Stripe CTA */}
                  {s.action && (
                    <button
                      className="vso-stripe-btn"
                      onClick={openOnboarding}
                      disabled={loadingLink}
                    >
                      <CreditCard size={16} />
                      {loadingLink ? "Preparing link..." : "Set up payouts with Stripe"}
                      {!loadingLink && <ExternalLink size={14} />}
                    </button>
                  )}

                  {/* Pending requirements */}
                  {s.n === 2 && connectStatus?.details_submitted && !isFullyConnected && hasRequirements && (
                    <div className="vso-requirements">
                      <p className="vso-req-title">Still needed by Stripe</p>
                      {connectStatus.requirements.map(req => (
                        <div className="vso-req-item" key={req}>
                          <AlertCircle size={13} color="var(--amber)" />
                          <span>{req.replace(/_/g, " ").replace(/\./g, " › ")}</span>
                        </div>
                      ))}
                      <button
                        className="vso-stripe-btn"
                        style={{ marginTop: 12, fontSize: 13 }}
                        onClick={openOnboarding}
                        disabled={loadingLink}
                      >
                        Complete verification <ChevronRight size={14} />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Fees card */}
          <div className="vso-info-card" data-testid="vendor-fees-card">
            <div className="vso-info-card-header">
              <p className="vso-info-card-title">Your selling fees</p>
              <button
                className={`vso-refresh-btn ${loadingStatus ? "spinning" : ""}`}
                onClick={fetchStatus}
              >
                <RefreshCw size={14} /> Refresh
              </button>
            </div>
            <div className="vso-info-row">
              <span className="vso-info-label">Transaction fee</span>
              <span className="vso-info-value" data-testid="vendor-transaction-fee">
                {`${vendor?.platform_fee_percent ?? 6.5}% of item + shipping`}
              </span>
            </div>
            <div className="vso-info-row">
              <span className="vso-info-label">Card processing</span>
              <span className="vso-info-value">Paid by the buyer</span>
            </div>
            <div className="vso-info-row">
              <span className="vso-info-label">Monthly fee</span>
              <span className="vso-info-value">None</span>
            </div>
            <div className="vso-info-row">
              <span className="vso-info-label">Payout schedule</span>
              <span className="vso-info-value">Automatic after each order</span>
            </div>
            <div className="vso-info-row">
              <span className="vso-info-label">Stripe status</span>
              <span className="vso-info-value" style={{ color: isFullyConnected ? "var(--sage)" : "var(--amber)" }}>
                {loadingStatus ? "Checking..." : isFullyConnected ? "Active" : "Setup required"}
              </span>
            </div>
          </div>

          {/* Trust badges */}
          <div className="vso-trust">
            <div className="vso-trust-item">
              <div className="vso-trust-icon"><Shield size={16} /></div>
              <span>Bank-level<br />security</span>
            </div>
            <div className="vso-trust-item">
              <div className="vso-trust-icon"><Zap size={16} /></div>
              <span>Instant<br />transfers</span>
            </div>
            <div className="vso-trust-item">
              <div className="vso-trust-icon"><CreditCard size={16} /></div>
              <span>135+ currencies<br />supported</span>
            </div>
          </div>

          {error && (
            <div style={{
              background: "#fdf0ee",
              border: "1px solid #f1b8b2",
              borderRadius: 8,
              padding: "12px 16px",
              fontSize: 13,
              color: "#c0392b",
              marginTop: 8,
            }}>
              {error}
            </div>
          )}

        </div>
      </div>
    </>
  );
}
